/*
 * ArtSHFT parallax engine
 * Single rAF loop, IntersectionObserver-driven, reduced-motion aware.
 *
 * Markup contract:
 *   <div class="parallax" data-parallax-speed="0.4">...</div>
 *   <div class="reveal" data-reveal="up" data-reveal-delay="100">...</div>
 *   <header class="js-transparent-header" data-transparent-until="500">...</header>
 *
 * Theme settings exposed on window.ArtSHFT:
 *   { enableParallax, enableSmoothScroll, enableScrollReveals, parallaxIntensity }
 */
(function () {
  'use strict';

  var settings = (window.ArtSHFT && window.ArtSHFT.motion) || {};
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var intensity = (typeof settings.parallaxIntensity === 'number' ? settings.parallaxIntensity : 50) / 100;

  // ---------------- Parallax (background/layer translation) ----------------

  var parallaxItems = [];
  var visibleItems = new Set();
  var rafScheduled = false;
  var lastScrollY = window.scrollY || 0;

  function registerParallaxItem(el) {
    var speed = parseFloat(el.dataset.parallaxSpeed);
    if (isNaN(speed)) speed = 0.3;
    var item = {
      el: el,
      speed: speed * intensity,
      lastY: 0
    };
    parallaxItems.push(item);
    parallaxObserver.observe(el);
    return item;
  }

  var parallaxObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var item = parallaxItems.find(function (i) { return i.el === entry.target; });
      if (!item) return;
      if (entry.isIntersecting) {
        visibleItems.add(item);
        scheduleUpdate();
      } else {
        visibleItems.delete(item);
      }
    });
  }, { rootMargin: '20% 0px 20% 0px', threshold: 0 });

  function updateParallax() {
    rafScheduled = false;
    if (!visibleItems.size) return;
    var viewportH = window.innerHeight;
    visibleItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      // Distance from viewport center, normalized so center=0
      var sectionCenter = rect.top + rect.height / 2;
      var viewportCenter = viewportH / 2;
      var delta = (sectionCenter - viewportCenter) * item.speed;
      // Round to whole pixels — avoids subpixel re-rasterization on some browsers
      var translateY = Math.round(delta * -1);
      if (translateY !== item.lastY) {
        item.el.style.transform = 'translate3d(0, ' + translateY + 'px, 0)';
        item.lastY = translateY;
      }
    });
  }

  function scheduleUpdate() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(updateParallax);
  }

  function onScroll() {
    if (Math.abs(window.scrollY - lastScrollY) < 1) return;
    lastScrollY = window.scrollY;
    scheduleUpdate();
  }

  // ---------------- Reveal-on-scroll ----------------

  var revealObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        var delay = parseInt(entry.target.dataset.revealDelay || '0', 10);
        if (delay) {
          setTimeout(function () { entry.target.classList.add('is-revealed'); }, delay);
        } else {
          entry.target.classList.add('is-revealed');
        }
        revealObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

  // ---------------- Marquee (CSS-driven, JS only for velocity coupling) ----------------

  var marquees = [];
  function registerMarquee(el) {
    var baseSpeed = parseFloat(el.dataset.marqueeSpeed) || 40; // seconds for one loop
    el.style.setProperty('--marquee-duration', baseSpeed + 's');
    marquees.push({ el: el });
  }

  // ---------------- Transparent-on-hero header ----------------

  function initTransparentHeader() {
    var header = document.querySelector('.js-transparent-header');
    if (!header) return;
    var threshold = parseInt(header.dataset.transparentUntil || '300', 10);
    function updateHeader() {
      if (window.scrollY > threshold) {
        header.classList.add('is-solid');
      } else {
        header.classList.remove('is-solid');
      }
    }
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  // ---------------- Public init ----------------

  function init() {
    if (prefersReducedMotion) {
      // Show every reveal element immediately and skip parallax wiring entirely
      document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-revealed'); });
      initTransparentHeader();
      return;
    }

    if (settings.enableParallax !== false) {
      document.querySelectorAll('[data-parallax-speed]').forEach(registerParallaxItem);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', scheduleUpdate, { passive: true });
      scheduleUpdate();
    }

    if (settings.enableScrollReveals !== false) {
      document.querySelectorAll('.reveal').forEach(function (el) { revealObserver.observe(el); });
    } else {
      document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-revealed'); });
    }

    document.querySelectorAll('[data-marquee-speed]').forEach(registerMarquee);

    initTransparentHeader();

    if (settings.enableSmoothScroll !== false && window.Lenis) {
      var lenis = new window.Lenis({ duration: 1.1, smoothWheel: true });
      function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
      // Lenis fires scroll events that bubble; our onScroll handles them
    }
  }

  // Mutation observer — pick up sections added by section reload during theme editor edits
  var domObserver = new MutationObserver(function (mutations) {
    if (prefersReducedMotion) return;
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('[data-parallax-speed]')) registerParallaxItem(node);
        node.querySelectorAll && node.querySelectorAll('[data-parallax-speed]').forEach(registerParallaxItem);
        if (node.matches && node.matches('.reveal')) revealObserver.observe(node);
        node.querySelectorAll && node.querySelectorAll('.reveal').forEach(function (el) { revealObserver.observe(el); });
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      domObserver.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    init();
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Expose for theme editor diagnostics
  window.ArtSHFTParallax = { register: registerParallaxItem, scheduleUpdate: scheduleUpdate };
})();

/* ---- Thumbnail column: smooth wheel scroll, contained to the list ---- */
(function () {
  'use strict';

  var targetTop = 0;
  var currentTop = 0;
  var rafId = null;
  var activeList = null;
  var EASE = 0.10; // lower = smoother/slower, higher = snappier

  function tick() {
    var diff = targetTop - currentTop;
    if (Math.abs(diff) < 0.5) {
      // Close enough — snap and stop
      activeList.scrollTop = targetTop;
      currentTop = targetTop;
      rafId = null;
      return;
    }
    currentTop += diff * EASE;
    activeList.scrollTop = currentTop;
    rafId = requestAnimationFrame(tick);
  }

  function init() {
    document.addEventListener('wheel', function (e) {
      var slider = e.target.closest('.thumbnail-slider');
      if (!slider) return;

      var list = slider.querySelector('.thumbnail-list');
      if (!list) return;

      // Only intercept when the list has overflow to scroll
      if (list.scrollHeight <= list.clientHeight) return;

      e.preventDefault();
      e.stopPropagation();

      // Normalise delta
      var delta = e.deltaY || e.deltaX;
      if (e.deltaMode === 1) delta *= 32;          // line → px
      if (e.deltaMode === 2) delta *= list.clientHeight; // page → px

      // If switching lists, reset position tracking
      if (list !== activeList) {
        activeList = list;
        currentTop = list.scrollTop;
        targetTop  = list.scrollTop;
      }

      // Accumulate target and clamp to valid range
      var maxScroll = list.scrollHeight - list.clientHeight;
      targetTop = Math.max(0, Math.min(maxScroll, targetTop + delta));

      // Kick off animation if not already running
      if (!rafId) rafId = requestAnimationFrame(tick);
    }, { passive: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* Image protection — block right-click save and drag on product/collection images */
(function() {
  function blockImageInteraction(e) {
    if (e.target.tagName === 'IMG') e.preventDefault();
  }
  document.addEventListener('contextmenu', blockImageInteraction);
  document.addEventListener('dragstart', blockImageInteraction);
})();


